import { useState, useMemo, Dispatch, SetStateAction } from 'react';
import { StockItem, Supplier } from '../types';
import { getDisplayName, calculateStockDays, compareStockHealth } from '../utils/stockUtils';

type SortKey = keyof StockItem | 'stock_days' | 'stock_health' | string;
type SortOrder = 'asc' | 'desc';

interface SortConfig {
  key: SortKey;
  order: SortOrder;
}

interface UseStockTableReturn {
  displayedData: StockItem[];
  sortConfig: SortConfig;
  searchTerm: string;
  supplierFilter: string;
  hideNAStock: boolean;
  handleSort: (key: SortKey) => void;
  setSearchTerm: Dispatch<SetStateAction<string>>;
  setSupplierFilter: Dispatch<SetStateAction<string>>;
  setHideNAStock: Dispatch<SetStateAction<boolean>>;
}

const useStockTable = (stockData: StockItem[], suppliers: Supplier[]): UseStockTableReturn => {
  // Opens worst-first: out of stock (biggest sellers first), then lowest health.
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'stock_health', order: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [supplierFilter, setSupplierFilter] = useState<string>('all');
  const [hideNAStock, setHideNAStock] = useState(true);

  // Sorting is derived from (stockData, sortConfig) so it always reflects the
  // latest config — the old imperative applySort() read a stale sortConfig
  // from its closure, making the first click sort with the previous config.
  const sortedData = useMemo(() => {
    const { key, order } = sortConfig;
    const sorted = [...stockData].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      // Stock Health has its own ordering rules (out-of-stock grouping), and it
      // returns the direction already applied.
      if (key === 'stock_health') {
        return compareStockHealth(a, b, order);
      }

      if (key === 'stock_days') {
        const aStockDays = parseInt(calculateStockDays(a), 10);
        const bStockDays = parseInt(calculateStockDays(b), 10);
        aValue = isNaN(aStockDays) ? (order === 'asc' ? Infinity : -Infinity) : aStockDays;
        bValue = isNaN(bStockDays) ? (order === 'asc' ? Infinity : -Infinity) : bStockDays;
      } else if (key === 'supplier_name') {
        aValue = a.supplier_name || '';
        bValue = b.supplier_name || '';
      } else if (key === 'avg_sales_per_month') {
        aValue = Number(a.avg_sales_per_month) || 0;
        bValue = Number(b.avg_sales_per_month) || 0;
      } else if (key === 'id') {
        aValue = Number(a.id) || 0;
        bValue = Number(b.id) || 0;
      } else if (key.startsWith('warehouse::')) {
        const warehouseId = parseInt(key.split('::')[1]);
        aValue = Number(a.warehouse_stocks[warehouseId]) || 0;
        bValue = Number(b.warehouse_stocks[warehouseId]) || 0;
      } else {
        aValue = key === 'name' ? getDisplayName(a, stockData, stockData) : a[key as keyof StockItem];
        bValue = key === 'name' ? getDisplayName(b, stockData, stockData) : b[key as keyof StockItem];
      }

      aValue = aValue ?? '';
      bValue = bValue ?? '';

      let cmp: number;
      if (typeof aValue === 'string' || typeof bValue === 'string') {
        cmp = String(aValue).toLowerCase().localeCompare(String(bValue).toLowerCase());
      } else {
        cmp = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      }
      return order === 'desc' ? -cmp : cmp;
    });
    return sorted;
  }, [stockData, sortConfig]);

  const handleSort = (key: SortKey) => {
    setSortConfig((prev) => ({
      key,
      order: prev.key === key && prev.order === 'asc' ? 'desc' : 'asc',
    }));
  };

  const displayedData = useMemo(() => {
    let data = sortedData;

    // Apply hide N/A stock filter
    if (hideNAStock) {
      data = data.filter((item) => item.stock_quantity !== null);
    }

    // Apply supplier filter
    if (supplierFilter !== 'all') {
      data = data.filter(
        (item) => item.supplier_name === supplierFilter || (!item.supplier_name && supplierFilter === 'None')
      );
    }

    // Apply search filter
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      data = data.filter((item) => {
        const displayName = getDisplayName(item, stockData, stockData);
        return [displayName || '', item.sku || '', String(item.id)]
          .join(' ')
          .toLowerCase()
          .includes(lowerSearchTerm);
      });
    }

    return data;
  }, [sortedData, searchTerm, supplierFilter, hideNAStock, stockData]);

  return {
    displayedData,
    sortConfig,
    searchTerm,
    supplierFilter,
    hideNAStock,
    handleSort,
    setSearchTerm,
    setSupplierFilter,
    setHideNAStock,
  };
};

export default useStockTable;
